import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { StockItem, Supplier, Warehouse, Tag } from '../types';
import { getDisplayName, calculateStockDays, calculateStockHealth, getStockHealthClass } from '../utils/stockUtils';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useStockTable from '../hooks/useStockTable';
import ProductDetailsDialog from './ProductDetailsDialog';
import '../core.css';
import LoadingSpinner from './LoadingSpinner';

type SortKey = keyof StockItem | 'stock_days' | 'stock_health' | string;
type SortOrder = 'asc' | 'desc';

interface StaticColumn {
  key: string;
  label: string;
  sortKey: SortKey;
}

interface WarehouseColumn {
  key: string;
  label: string;
  sortKey: SortKey;
  warehouseId: number;
}

type Column = StaticColumn | WarehouseColumn;

interface StockTableProps {
  isAdmin: boolean;
}

const useBeforeUnload = (shouldPrompt: boolean) => {
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldPrompt) {
        console.log('StockTable beforeunload triggered');
        e.preventDefault();
        e.returnValue = 'You have unsaved changes in the stock table. Do you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldPrompt]);
};

const StockTable: React.FC<StockTableProps> = ({ isAdmin }) => {
  const { user, permissions } = useAuth();
  const { showToast } = useToast();
  const [stockData, setStockData] = useState<StockItem[]>([]);
  const [originalStocks, setOriginalStocks] = useState<{
    [key: string]: {
      warehouse_stocks: { [warehouseId: number]: number };
      stock_quantity: number | null;
    };
  }>({});
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changedStocks, setChangedStocks] = useState<{
    [key: string]: {
      warehouse_stocks?: { [warehouseId: number]: number };
      target_stock_days?: number;
      supplier_name?: string;
      stock_quantity?: number | null;
      gtin?: string;
      tag_ids?: number[];
      estimated_restock_date?: string;
      reason?: string;
    };
  }>({});
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [lockGtinFields, setLockGtinFields] = useState(false);
  const [activeSupplierDialog, setActiveSupplierDialog] = useState<string | null>(null);
  const [activeTagDialog, setActiveTagDialog] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<StockItem | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [columnSettings, setColumnSettings] = useState<{ column_name: string; is_visible: boolean }[]>([]);
  const [reasonDialog, setReasonDialog] = useState<boolean>(false);
  const [reasonValue, setReasonValue] = useState<string>('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const lastPathnameRef = useRef(currentLocation.pathname);
  const [showProductDialog, setShowProductDialog] = useState<StockItem | null>(null);

  const {
    displayedData,
    sortConfig,
    searchTerm,
    setSearchTerm,
    supplierFilter,
    setSupplierFilter,
    hideNAStock,
    setHideNAStock,
    handleSort,
  } = useStockTable(stockData, suppliers);

  useEffect(() => {
    if (!isAdmin && permissions?.visible_suppliers.length === 1 && supplierFilter === 'all') {
      setSupplierFilter(permissions.visible_suppliers[0]);
    }
  }, [isAdmin, permissions, supplierFilter, setSupplierFilter]);

  const isRegularUser = permissions?.user_type === 'Regular';
  const visibleSuppliers = isAdmin ? suppliers : suppliers.filter((s) => permissions?.visible_suppliers.includes(s.name));

  useBeforeUnload(isAdmin && Object.keys(changedStocks).length > 0);

  // Deep link from Dashboard: /stock?product=SKU opens the product details
  // editor for that item once stock data has loaded.
  useEffect(() => {
    const params = new URLSearchParams(currentLocation.search);
    const productSku = params.get('product');
    if (!productSku || stockData.length === 0) return;
    const match = stockData.find((item) => item.sku === productSku);
    if (match) {
      setShowProductDialog(match);
      setSearchTerm(productSku);
    }
    // Clear the param so closing the dialog doesn't re-open it
    navigate(currentLocation.pathname, { replace: true });
  }, [currentLocation.search, currentLocation.pathname, stockData, navigate, setSearchTerm]);

  useEffect(() => {
    if (lastPathnameRef.current === currentLocation.pathname) {
      return;
    }
    if (isAdmin && Object.keys(changedStocks).length > 0) {
      console.log('StockTable route change detected:', {
        from: lastPathnameRef.current,
        to: currentLocation.pathname,
        changedStocksCount: Object.keys(changedStocks).length,
      });
      const shouldLeave = window.confirm(
        'You have unsaved changes in the stock table. Do you want to save changes, cancel, or leave? Click OK to leave (changes will be cleared).'
      );
      if (!shouldLeave) {
        console.log('StockTable navigation cancelled, reverting to:', lastPathnameRef.current);
        navigate(lastPathnameRef.current, { replace: true });
        return;
      }
      console.log('StockTable navigation confirmed, clearing changedStocks');
      setChangedStocks({});
    }
    lastPathnameRef.current = currentLocation.pathname;
  }, [currentLocation.pathname, changedStocks, navigate, isAdmin]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        console.log('Closing dialog due to click outside');
        if (activeTagDialog && activeItem) {
          console.log('Auto-saving tags:', selectedTagIds, 'for item:', activeItem.id, activeItem.type);
          handleTagChange(activeItem, selectedTagIds);
        }
        setActiveSupplierDialog(null);
        setActiveTagDialog(null);
        setActiveItem(null);
        setSelectedTagIds([]);
        setReasonDialog(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeTagDialog, activeItem, selectedTagIds]);

  useEffect(() => {
    const fetchColumnSettings = async () => {
      try {
        const response = await api.get('/column-settings');
        setColumnSettings(response.data.columns);
      } catch (error: any) {
        console.error('Fetch column settings error:', error.message);
        showToast('Failed to fetch column settings', 'error');
      }
    };
    fetchColumnSettings();
  }, [showToast]);

  const fetchNeonStock = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api.get('/neon-stock');
      const newStockData = response.data.data;
      setStockData(newStockData);
      setSuppliers(response.data.suppliers);
      setWarehouses(response.data.warehouses);
      setLastUpdated(response.data.last_updated);
      setError(null);
      // Store original stock values
      const newOriginalStocks: typeof originalStocks = {};
      newStockData.forEach((item: StockItem) => {
        const key = `${item.id}-${item.type}`;
        newOriginalStocks[key] = {
          warehouse_stocks: { ...item.warehouse_stocks },
          stock_quantity: item.stock_quantity,
        };
      });
      console.log('Fetched original stocks:', newOriginalStocks);
      setOriginalStocks(newOriginalStocks);
    } catch (error: any) {
      console.error('Fetch neon stock error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Unauthorized, redirecting to /login');
        navigate('/login');
      } else {
        setError(error.response?.data?.error || error.message || 'Failed to fetch stock data');
        showToast(error.response?.data?.error || error.message || 'Failed to fetch stock data', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await api.get('/product-tags');
      const mappedTags = response.data.tags.map((tag: any) => ({
        id: tag.id,
        name: tag.tag_name,
        color: tag.color,
      }));
      console.log('Fetched tags:', mappedTags);
      setTags(mappedTags);
    } catch (error: any) {
      console.error('Fetch tags error:', error.message);
      showToast('Failed to fetch tags', 'error');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setLockGtinFields(response.data.lock_gtin_fields);
    } catch (error: any) {
      console.error('Fetch settings error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Unauthorized, redirecting to /login');
        navigate('/login');
      } else {
        setError(error.response?.data?.error || error.message || 'Failed to fetch settings');
        showToast(error.response?.data?.error || error.message || 'Failed to fetch settings', 'error');
      }
    }
  };

  useEffect(() => {
    if (!user) {
      console.log('No user, redirecting to /login');
      navigate('/login');
      return;
    }
    const initializeData = async () => {
      await fetchTags();
      await fetchSettings();
      await fetchNeonStock();
    };
    initializeData();
  }, [user, navigate, showToast]);

  const pullDataFromWooCommerce = async () => {
    if (!isAdmin) return;
    setUpdating(true);
    try {
      const response = await api.post('/pull-woocommerce');
      setStockData(response.data.data);
      setLastUpdated(response.data.last_updated);
      setChangedStocks({});
      await fetchNeonStock();
      showToast('Data pulled from WooCommerce successfully', 'success');
    } catch (error: any) {
      console.error('Pull WooCommerce error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Unauthorized, redirecting to /login');
        navigate('/login');
      } else {
        setError(error.response?.data?.error || error.message || 'Failed to pull WooCommerce data');
        showToast(error.response?.data?.error || error.message || 'Failed to pull WooCommerce data', 'error');
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleWarehouseStockChange = (item: StockItem, warehouseId: number, value: string) => {
    const warehouseName = warehouses.find(w => w.id === warehouseId)?.name || '';
    if (!isAdmin && !permissions?.editable_warehouses.includes(warehouseName)) return;
    const key = `${item.id}-${item.type}`;
    let newValue: number;
    if (value === '' || isNaN(parseInt(value, 10))) {
      newValue = 0;
    } else {
      newValue = parseInt(value, 10);
      if (newValue < -50) {
        showToast('Stock cannot be less than -50.', 'error');
        return;
      }
    }
    setChangedStocks((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        warehouse_stocks: {
          ...(prev[key]?.warehouse_stocks || {}),
          [warehouseId]: newValue,
        },
      },
    }));
  };

  const handleTotalStockChange = (item: StockItem, value: string) => {
    if (!isAdmin) return;
    const key = `${item.id}-${item.type}`;
    let newValue: number | null;
    if (value === '' || isNaN(parseInt(value, 10))) {
      newValue = null;
    } else {
      newValue = parseInt(value, 10);
      if (newValue < -50) {
        showToast('Stock cannot be less than -50.', 'error');
        return;
      }
    }
    setChangedStocks((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        stock_quantity: newValue,
      },
    }));
  };

  // The nightly sync raises Total Stock to (warehouse stock - unshipped orders) and
  // pushes it to WooCommerce, and it only ever raises. Lowering Total Stock below the
  // warehouse total therefore gets undone overnight unless warehouse stock is lowered
  // too. Deprecated warehouses are excluded to match the sync's own total.
  const getRaiseBackWarnings = () => {
    const warnings: { sku: string; name: string; total: number; warehouseTotal: number }[] = [];
    for (const [key, changes] of Object.entries(changedStocks)) {
      if (changes.stock_quantity === undefined || changes.stock_quantity === null) continue;
      const [itemId, itemType] = key.split('-');
      const item = stockData.find(
        (dataItem) => dataItem.id === parseInt(itemId) && dataItem.type === itemType
      );
      if (!item) continue;
      const warehouseTotal = warehouses
        .filter((w) => w.name !== 'deprecated')
        .reduce(
          (sum, w) => sum + Number(changes.warehouse_stocks?.[w.id] ?? item.warehouse_stocks[w.id] ?? 0),
          0
        );
      if (changes.stock_quantity < warehouseTotal) {
        warnings.push({
          sku: item.sku,
          name: getDisplayName(item, stockData, stockData),
          total: changes.stock_quantity,
          warehouseTotal,
        });
      }
    }
    return warnings;
  };

  const handleReasonSubmit = async (reason: string) => {
    if (!reason.trim()) {
      showToast('Reason is required for stock changes.', 'error');
      return;
    }
    setSaving(true);

    // Build WooCommerce push payload from any total-stock changes
    const stockUpdates: any[] = [];
    for (const [key, changes] of Object.entries(changedStocks)) {
      if (changes.stock_quantity === undefined || changes.stock_quantity === null) continue;
      const [itemId, itemType] = key.split('-');
      const item = stockData.find(
        (dataItem) => dataItem.id === parseInt(itemId) && dataItem.type === itemType
      );
      if (!item) continue;
      if (changes.stock_quantity < -50) {
        showToast(`Total stock cannot be less than -50 for ${item.sku}`, 'error');
        setSaving(false);
        return;
      }
      stockUpdates.push({
        product_id: item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id,
        variation_id: item.type === 'variation' ? item.id : 0,
        stock_quantity: changes.stock_quantity,
      });
    }
    const shouldPush = stockUpdates.length > 0;

    setReasonDialog(false);
    setReasonValue('');
    const saveSuccess = await handleSaveAllWithReason(reason);

    if (saveSuccess) {
      if (shouldPush) {
        try {
          await api.post('/push-woocommerce', { updates: stockUpdates });
          showToast('Stock saved and pushed to WooCommerce successfully', 'success');
        } catch (error: any) {
          console.error('Push WooCommerce error:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
          });
          if (error.response?.status === 401 || error.response?.status === 403) {
            navigate('/login');
            setSaving(false);
            return;
          }
          showToast('Stock saved but failed to push to WooCommerce', 'error');
        }
      } else {
        showToast('Stock updated successfully', 'success');
      }
    }

    setSaving(false);
  };

  const handleTargetStockDaysChange = (item: StockItem, value: string) => {
    if (!isAdmin) return;
    const key = `${item.id}-${item.type}`;
    let newValue: number;
    if (value === '' || isNaN(parseInt(value, 10))) {
      newValue = 0;
    } else {
      newValue = parseInt(value, 10);
      if (newValue < 0) {
        showToast('Target stock days cannot be negative.', 'error');
        return;
      }
    }
    setChangedStocks((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        target_stock_days: newValue,
      },
    }));
  };

  const handleEstimatedRestockDateChange = (item: StockItem, value: string) => {
    if (!isAdmin) return;
    const key = `${item.id}-${item.type}`;
    setChangedStocks((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        estimated_restock_date: value || undefined,
      },
    }));
  };

  const handleSupplierChange = (item: StockItem, supplierName: string) => {
    if (!isAdmin) return;
    // 'None' is the sentinel the update-stock API maps to NULL. Staging
    // undefined here would make the save loop silently drop the change.
    const supplierValue = supplierName === '0' ? 'None' : supplierName;
    const key = `${item.id}-${item.type}`;
    setChangedStocks((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        supplier_name: supplierValue,
      },
    }));
  };

  const handleGtinChange = (item: StockItem, value: string) => {
    if (!isAdmin) return;
    if (value.length > 14) {
      showToast('GTIN cannot exceed 14 characters.', 'error');
      return;
    }
    const key = `${item.id}-${item.type}`;
    setChangedStocks((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        gtin: value || undefined,
      },
    }));
  };

  const handleTagChange = (item: StockItem, tagIds: number[]) => {
    if (!isAdmin) return;
    const key = `${item.id}-${item.type}`;
    setChangedStocks((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        tag_ids: tagIds,
      },
    }));
  };

  const saveTagsToApi = async (item: StockItem, tagIds: number[]) => {
    if (!isAdmin) return;
    try {
      const productId = item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id;
      const variationId = item.type === 'variation' ? item.id : null;
      console.log('Saving tags to API:', { productId, variationId, tagIds });
      await api.post('/product-tags', {
        product_id: productId,
        variation_id: variationId,
        tag_ids: tagIds,
      });
      const key = `${item.id}-${item.type}`;
      setChangedStocks((prev) => {
        const newChanges = { ...prev };
        delete newChanges[key]?.tag_ids;
        if (Object.keys(newChanges[key] || {}).length === 0) delete newChanges[key];
        return newChanges;
      });
    } catch (error: any) {
      console.error('Save tags error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      showToast(`Failed to save tags: ${error.message}`, 'error');
    }
  };

  const validateChanges = (changes: { [key: string]: any }, item: StockItem): boolean => {
    const stockValues = [
      ...(changes.warehouse_stocks
        ? Object.values(changes.warehouse_stocks)
        : Object.values(item.warehouse_stocks)),
      changes.target_stock_days !== undefined ? changes.target_stock_days : item.target_stock_days,
    ];
    for (const fieldValue of stockValues) {
      const numericValue = Number(fieldValue);
      if (isNaN(numericValue) || numericValue < -50) {
        showToast('Stock fields cannot be less than -50.', 'error');
        return false;
      }
      if (fieldValue === '' || fieldValue === null) {
        showToast('Stock fields cannot be blank.', 'error');
        return false;
      }
    }
    return true;
  };

  const handleSaveAll = async () => {
    if (!isAdmin && (permissions?.editable_warehouses?.length ?? 0) === 0) {
      console.log('No permissions to save changes');
      showToast('No permissions to save changes.', 'error');
      return;
    }
    if (Object.keys(changedStocks).length === 0) {
      console.log('No changes to save');
      showToast('No changes to save.', 'error');
      return;
    }

    // Check if there are stock changes requiring a reason
    const hasStockChanges = Object.values(changedStocks).some(
      (changes) => changes.warehouse_stocks || changes.stock_quantity !== undefined
    );
    console.log('Has stock changes:', hasStockChanges);

    if (hasStockChanges) {
      console.log('Opening reason dialog');
      setReasonValue('');
      setReasonDialog(true);
      return;
    }

    console.log('No stock changes, saving without reason');
    setSaving(true);
    try {
      const saveSuccess = await handleSaveAllWithReason('No stock changes');
      if (saveSuccess) {
        showToast('Changes saved successfully', 'success');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAllWithReason = async (reason: string): Promise<boolean> => {
    console.log('Starting handleSaveAllWithReason with reason:', reason);
    const warehouseUpdates: any[] = [];
    const targetStockDaysUpdates: any[] = [];
    const supplierUpdates: any[] = [];
    const gtinUpdates: any[] = [];
    const estimatedRestockDateUpdates: any[] = [];
    const stockTransactions: any[] = [];

    for (const [key, changes] of Object.entries(changedStocks)) {
      console.log(`Processing changes for key: ${key}`, changes);
      const [itemId, itemType] = key.split('-');
      const item = stockData.find(
        (dataItem) => dataItem.id === parseInt(itemId) && dataItem.type === itemType
      );
      if (!item) {
        console.log(`Item not found for key: ${key}`);
        continue;
      }
      if (!validateChanges(changes, item)) {
        console.log(`Validation failed for item: ${item.id}-${item.type}`);
        return false;
      }
      if (changes.warehouse_stocks) {
        console.log(`Processing warehouse_stocks for item: ${item.id}`, changes.warehouse_stocks);
        Object.entries(changes.warehouse_stocks).forEach(([warehouseId, stockValue]) => {
          const warehouseName = warehouses.find(w => w.id === parseInt(warehouseId))?.name || '';
          if (!isAdmin && !permissions?.editable_warehouses.includes(warehouseName)) {
            console.log(`Skipping warehouse ${warehouseId} due to permissions`);
            return;
          }
          const stockQuantity = Number(stockValue);
          console.log(`Warehouse ${warehouseId} stock value: ${stockQuantity}`);
          if (!isNaN(stockQuantity)) {
            const originalStock = originalStocks[key]?.warehouse_stocks[parseInt(warehouseId)] ?? 0;
            const quantityChange = Math.abs(stockQuantity - originalStock);
            console.log(`Warehouse ${warehouseId} - Original: ${originalStock}, New: ${stockQuantity}, Change: ${quantityChange}`);
            if (quantityChange > 0) {
              const transactionType = stockQuantity > originalStock ? 'ADD' : 'REMOVE';
              console.log(`Transaction type for warehouse ${warehouseId}: ${transactionType}`);
              stockTransactions.push({
                product_id: item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id,
                variation_id: item.type === 'variation' ? item.id : 0,
                warehouse_id: parseInt(warehouseId),
                quantity: quantityChange,
                reason,
                transaction_type: transactionType,
              });
            }
            warehouseUpdates.push({
              product_id: item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id,
              variation_id: item.type === 'variation' ? item.id : 0,
              warehouse_id: parseInt(warehouseId),
              stock_quantity: Math.max(stockQuantity, -50),
            });
          } else {
            console.log(`Invalid stock quantity for warehouse ${warehouseId}: ${stockValue}`);
          }
        });
      }
      if (changes.stock_quantity !== undefined) {
        console.log(`Processing stock_quantity for item: ${item.id}`, changes.stock_quantity);
        const stockQuantity = changes.stock_quantity;
        if (stockQuantity !== null) {
          const originalStock = originalStocks[key]?.stock_quantity ?? 0;
          const quantityChange = Math.abs(stockQuantity - originalStock);
          console.log(`Total stock - Original: ${originalStock}, New: ${stockQuantity}, Change: ${quantityChange}`);
          warehouseUpdates.push({
            product_id: item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id,
            variation_id: item.type === 'variation' ? item.id : 0,
            warehouse_id: 0,
            stock_quantity: Math.max(stockQuantity, -50),
          });
        } else {
          console.log(`Setting stock_quantity to null for item: ${item.id}`);
          warehouseUpdates.push({
            product_id: item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id,
            variation_id: item.type === 'variation' ? item.id : 0,
            warehouse_id: 0,
            stock_quantity: null,
          });
        }
      }
      if (changes.target_stock_days !== undefined) {
        const targetDays = Number(changes.target_stock_days);
        console.log(`Adding target_stock_days update: ${targetDays}`);
        targetStockDaysUpdates.push({
          product_id: item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id,
          variation_id: item.type === 'variation' ? item.id : 0,
          target_stock_days: isNaN(targetDays) ? 0 : Math.max(targetDays, 0),
        });
      }
      if (changes.supplier_name !== undefined) {
        console.log(`Adding supplier update: ${changes.supplier_name}`);
        supplierUpdates.push({
          product_id: item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id,
          variation_id: item.type === 'variation' ? item.id : 0,
          supplier_name: changes.supplier_name,
        });
      }
      if (changes.gtin !== undefined) {
        console.log(`Adding gtin update: ${changes.gtin}`);
        gtinUpdates.push({
          product_id: item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id,
          variation_id: item.type === 'variation' ? item.id : 0,
          gtin: changes.gtin,
        });
      }
      if (changes.estimated_restock_date !== undefined) {
        console.log(`Adding estimated_restock_date update: ${changes.estimated_restock_date}`);
        estimatedRestockDateUpdates.push({
          product_id: item.type === 'variation' && item.parent_product_id ? item.parent_product_id : item.id,
          variation_id: item.type === 'variation' ? item.id : 0,
          estimated_restock_date: changes.estimated_restock_date,
          website_message: null,
        });
      }
    }

    console.log('Final updates to send:', {
      warehouseUpdates,
      targetStockDaysUpdates,
      supplierUpdates,
      gtinUpdates,
      estimatedRestockDateUpdates,
      stockTransactions,
    });

    try {
      // Update stock
      if (warehouseUpdates.length > 0 || targetStockDaysUpdates.length > 0 ||
          supplierUpdates.length > 0 || gtinUpdates.length > 0) {
        console.log('Sending /update-stock API call with', warehouseUpdates.length, 'warehouse updates');
        await api.post('/update-stock', {
          warehouse_updates: warehouseUpdates,
          target_stock_days_updates: targetStockDaysUpdates,
          supplier_updates: supplierUpdates,
          gtin_updates: gtinUpdates,
        });
        console.log('/update-stock API call successful');
      } else {
        console.log('No updates to send to /update-stock');
      }

      // Update estimated restock dates
      if (estimatedRestockDateUpdates.length > 0) {
        console.log('Sending estimated restock date updates');
        for (const update of estimatedRestockDateUpdates) {
          await api.post('/update-estimated-restock-date', update);
        }
        console.log('Estimated restock date updates successful');
      }

      // Log stock transactions
      if (stockTransactions.length > 0) {
        console.log(`Sending ${stockTransactions.length} /add-stock-transaction API calls`);
        for (const transaction of stockTransactions) {
          console.log('Sending /add-stock-transaction:', transaction);
          try {
            await api.post('/add-stock-transaction', transaction);
            console.log('Successfully logged transaction:', transaction);
          } catch (error: any) {
            console.error('Failed to log stock transaction:', {
              transaction,
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
            });
            showToast(`Failed to log stock transaction for product ${transaction.product_id}: ${error.message}`, 'error');
          }
        }
      } else {
        console.log('No stock transactions to log');
      }

      // Apply changes to local state immediately — no network refresh needed
      const updatedStockData = stockData.map(item => {
        const key = `${item.id}-${item.type}`;
        const changes = changedStocks[key];
        if (!changes) return item;
        return {
          ...item,
          ...(changes.warehouse_stocks && {
            warehouse_stocks: { ...item.warehouse_stocks, ...changes.warehouse_stocks },
          }),
          ...(changes.stock_quantity !== undefined && { stock_quantity: changes.stock_quantity }),
          ...(changes.target_stock_days !== undefined && { target_stock_days: changes.target_stock_days }),
          ...(changes.supplier_name !== undefined && { supplier_name: changes.supplier_name }),
          ...(changes.gtin !== undefined && { gtin: changes.gtin }),
          ...(changes.estimated_restock_date !== undefined && { estimated_restock_date: changes.estimated_restock_date }),
        };
      });
      const newOriginalStocks: typeof originalStocks = {};
      updatedStockData.forEach(item => {
        const key = `${item.id}-${item.type}`;
        newOriginalStocks[key] = {
          warehouse_stocks: { ...item.warehouse_stocks },
          stock_quantity: item.stock_quantity,
        };
      });
      // Set stockData and originalStocks before clearing changedStocks to avoid value flash
      setStockData(updatedStockData);
      setOriginalStocks(newOriginalStocks);
      setChangedStocks({});
      console.log('Stock update completed successfully');
      return true;
    } catch (error: any) {
      console.error('Update stock error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Unauthorized, redirecting to /login');
        navigate('/login');
      } else {
        showToast(`Failed to update stock: ${error.message}`, 'error');
      }
      return false;
    }
  };

  const openSupplierDialog = (item: StockItem, event: React.MouseEvent) => {
    if (!isAdmin) return;
    event.stopPropagation();
    const key = `${item.id}-${item.type}`;
    console.log('Opening supplier dialog for:', key);
    setActiveSupplierDialog(key);
    setActiveItem(item);
  };

  const openTagDialog = (item: StockItem, event: React.MouseEvent) => {
    if (!isAdmin) return;
    event.stopPropagation();
    const key = `${item.id}-${item.type}`;
    console.log('Opening tag dialog for:', key);
    setActiveTagDialog(key);
    setActiveItem(item);
    setSelectedTagIds(item.tags.map((t) => t.id));
  };

  const closeDialog = () => {
    console.log('Closing dialog');
    setActiveSupplierDialog(null);
    setActiveTagDialog(null);
    setActiveItem(null);
    setSelectedTagIds([]);
    setReasonDialog(false);
    setReasonValue('');
  };

  const cancelDialog = () => {
    console.log('Canceling dialog');
    setActiveSupplierDialog(null);
    setActiveTagDialog(null);
    setActiveItem(null);
    setSelectedTagIds([]);
    setReasonDialog(false);
    setReasonValue('');
  };

  const handleSupplierSelect = (supplierName: string) => {
    if (activeItem) {
      console.log('Selected supplier:', supplierName, 'for item:', activeItem.id, activeItem.type);
      handleSupplierChange(activeItem, supplierName);
    }
    closeDialog();
  };

  const handleTagToggle = (tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleTagSave = async () => {
    if (activeItem) {
      console.log('Saving tags:', selectedTagIds, 'for item:', activeItem.id, activeItem.type);
      await saveTagsToApi(activeItem, selectedTagIds);
    }
    closeDialog();
  };

  console.log('Suppliers available:', suppliers);
  console.log('Warehouses available:', warehouses);
  console.log('Tags available:', tags);

  const staticColumnsBefore: StaticColumn[] = [
    { key: 'product_name', label: 'Product Name', sortKey: 'name' as SortKey },
    { key: 'id', label: 'Product ID', sortKey: 'id' as SortKey },
    { key: 'gtin', label: 'GTIN', sortKey: 'gtin' as SortKey },
    { key: 'sku', label: 'SKU', sortKey: 'sku' as SortKey },
    { key: 'supplier', label: 'Supplier', sortKey: 'supplier_name' as SortKey },
    { key: 'tags', label: 'Tags', sortKey: 'tags' as SortKey },
    { key: 'total_stock', label: 'Total Stock', sortKey: 'stock_quantity' as SortKey },
  ];

  const warehouseColumns: WarehouseColumn[] = warehouses.map((warehouse) => ({
    key: `warehouse_${warehouse.id}`,
    label: `${warehouse.name} Stock`,
    sortKey: `warehouse::${warehouse.id}` as SortKey,
    warehouseId: warehouse.id,
  }));

  const staticColumnsAfter: StaticColumn[] = [
    { key: 'avg_sales_per_month', label: 'Monthly Sales', sortKey: 'avg_sales_per_month' as SortKey },
    { key: 'target_stock_days', label: 'Target Stock (Days)', sortKey: 'target_stock_days' as SortKey },
    { key: 'stock_days', label: 'Stock (Days)', sortKey: 'stock_days' as SortKey },
    { key: 'stock_health', label: 'Stock Health', sortKey: 'stock_health' as SortKey },
    { key: 'product_message', label: 'Product Message', sortKey: 'website_message' as SortKey },
    { key: 'estimated_restock_date', label: 'Est Restock Date', sortKey: 'estimated_restock_date' as SortKey },
  ];

  const columns: Column[] = [...staticColumnsBefore, ...warehouseColumns, ...staticColumnsAfter];

  const visibleColumns = columns.filter((col) =>
    columnSettings.find((setting) => setting.column_name === col.key)?.is_visible ?? true
  );

  const hasStockQuantityChanges = Object.values(changedStocks).some(
    (changes) => changes.stock_quantity !== undefined
  );

  const hasTagChanges = Object.values(changedStocks).some(
    (changes) => changes.tag_ids !== undefined
  );

  if (error) return (
    <div className="container">
      <p>Error: {error}</p>
      <button onClick={() => fetchNeonStock()} className="action-button">Retry</button>
    </div>
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="stock-table-container">
      <h1>Stock</h1>
      <p className="last-updated">Last Updated: {lastUpdated}</p>
      <div className="filter-container">
        <label>
          <input
            type="checkbox"
            checked={hideNAStock}
            onChange={(e) => setHideNAStock(e.target.checked)}
          />
          Hide products with N/A stock
        </label>
      </div>
      {isAdmin && (
        <div className="pull-data-container">
          <button onClick={pullDataFromWooCommerce} className="action-button">
            Pull Data
          </button>
          {updating && <span className="updating-text">Updating...</span>}
        </div>
      )}
      <div className="search-container">
        <input
          type="text"
          placeholder="Search (e.g., MPP-TM3-RUCA)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={supplierFilter}
          onChange={(e) => setSupplierFilter(e.target.value)}
          className="supplier-filter"
        >
          {isAdmin && (
            <>
              <option value="all">All Suppliers</option>
              <option value="None">None</option>
            </>
          )}
          {visibleSuppliers.map((supplier) => (
            <option key={supplier.id} value={supplier.name}>
              {supplier.name}
            </option>
          ))}
        </select>
      </div>
      <div className="table-header">
        <p>Total Items: {displayedData.length}</p>
        {(isAdmin || (permissions?.editable_warehouses?.length ?? 0) > 0) && (
          <div>
            {Object.keys(changedStocks).length > 0 && (
              <button onClick={handleSaveAll} className={`action-button${hasStockQuantityChanges && isAdmin ? ' push-button' : ''}`} disabled={saving || updating}>
                {saving ? 'Saving...' : hasStockQuantityChanges && isAdmin ? 'Save & Sync WooCommerce' : 'Save'}
              </button>
            )}
          </div>
        )}
      </div>
      <table>
        <thead>
          <tr>
            {visibleColumns.map((col) => (
              <th key={col.key} onClick={() => handleSort(col.sortKey)}>
                {col.label} {sortConfig.key === col.sortKey && (sortConfig.order === 'asc' ? '↑' : '↓')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayedData.map((item) => {
            const displayName = getDisplayName(item, displayedData, stockData);
            const [productName, variationPart] = displayName.includes('(')
              ? displayName.split(' (')
              : [displayName, ''];
            const variationName = variationPart ? `(${variationPart}` : '';
            const supplierKey = `${item.id}-${item.type}`;
            // Show the staged (unsaved) supplier if one is pending
            const stagedSupplierName = changedStocks[supplierKey]?.supplier_name;
            const isSupplierChanged = stagedSupplierName !== undefined;
            const effectiveSupplierName = isSupplierChanged ? stagedSupplierName : item.supplier_name;
            const supplier = effectiveSupplierName && effectiveSupplierName !== 'None'
              ? suppliers.find((s) => s.name === effectiveSupplierName)
              : null;
            const tagKey = `${item.id}-${item.type}`;
            const tagColor = supplier?.color || '#e0e0e0';
            const isStockChanged = changedStocks[`${item.id}-${item.type}`]?.stock_quantity !== undefined;
            const isGtinChanged = changedStocks[`${item.id}-${item.type}`]?.gtin !== undefined;
            const isTagChanged = changedStocks[`${item.id}`]?.tag_ids !== undefined;
            return (
              <tr key={`${item.id}-${item.parent_product_id || 'null'}`}>
                {visibleColumns.map((col) => (
                  <td key={col.key} data-label={col.label}>
                    {col.key === 'product_name' ? (
                      <span
                        className="product-name"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowProductDialog(item)}
                      >
                        {productName}
                        {variationName && <span className="variation-text">{` ${variationName}`}</span>}
                      </span>
                    ) : col.key === 'id' ? (
                      item.id
                    ) : col.key === 'gtin' ? (
                      lockGtinFields || !isAdmin ? (
                        <span>{item.gtin || ''}</span>
                      ) : (
                        <input
                          type="text"
                          maxLength={14}
                          value={changedStocks[`${item.id}-${item.type}`]?.gtin || item.gtin || ''}
                          onChange={(e) => handleGtinChange(item, e.target.value)}
                          className={`stock-input ${isGtinChanged ? 'changed-stock' : ''}`}
                        />
                      )
                    ) : col.key === 'sku' ? (
                      item.sku
                    ) : col.key === 'supplier' ? (
                      <div className="supplier-tag-container">
                        <span
                          className="supplier-tag"
                          style={{
                            backgroundColor: tagColor,
                            cursor: isAdmin ? 'pointer' : 'default',
                            boxShadow: isSupplierChanged ? '0 0 0 2px var(--color-danger, #C0392B)' : undefined,
                          }}
                          title={isSupplierChanged ? 'Unsaved change — click Save All to apply' : undefined}
                          onClick={(e) => isAdmin && openSupplierDialog(item, e)}
                        >
                          {(effectiveSupplierName === 'None' ? '' : effectiveSupplierName) || 'None'}
                        </span>
                      </div>
                    ) : col.key === 'tags' ? (
                      <div className="tag-container">
                        {item.tags.length > 0 ? (
                          item.tags.map((tag) => (
                            <span
                              key={tag.id}
                              className="tag-chip"
                              style={{ backgroundColor: tag.color }}
                              onClick={(e) => isAdmin && openTagDialog(item, e)}
                            >
                              {tag.name}
                            </span>
                          ))
                        ) : (
                          <span
                            className="add-tag"
                            onClick={(e) => isAdmin && openTagDialog(item, e)}
                          >
                            +
                          </span>
                        )}
                      </div>
                    ) : col.key === 'total_stock' ? (
                      <input
                        type="number"
                        min="-50"
                        value={
                          changedStocks[`${item.id}-${item.type}`]?.stock_quantity !== undefined
                            ? changedStocks[`${item.id}-${item.type}`].stock_quantity ?? 0
                            : item.stock_quantity ?? 0
                        }
                        onChange={(e) => handleTotalStockChange(item, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        className={`stock-input ${isStockChanged ? 'changed-stock' : ''}`}
                        disabled={!isAdmin}
                      />
                    ) : col.key.startsWith('warehouse_') && 'warehouseId' in col ? (
                      <input
                        type="number"
                        min="-50"
                        value={
                          changedStocks[`${item.id}-${item.type}`]?.warehouse_stocks?.[col.warehouseId] ??
                          item.warehouse_stocks[col.warehouseId] ??
                          0
                        }
                        onChange={(e) => handleWarehouseStockChange(item, col.warehouseId, e.target.value)}
                        disabled={
                          item.stock_quantity === null ||
                          (!isAdmin && (!isRegularUser || !permissions?.editable_warehouses.includes(warehouses.find(w => w.id === col.warehouseId)?.name || '')))
                        }
                        className={`stock-input ${
                          changedStocks[`${item.id}-${item.type}`]?.warehouse_stocks?.[col.warehouseId] !== undefined
                            ? 'changed-stock'
                            : ''
                        }`}
                      />
                    ) : col.key === 'avg_sales_per_month' ? (
                      <span>{typeof item.avg_sales_per_month === 'number' ? item.avg_sales_per_month.toFixed(1) : Number(item.avg_sales_per_month || 0).toFixed(1)}</span>
                    ) : col.key === 'target_stock_days' ? (
                      <input
                        type="number"
                        min="0"
                        value={changedStocks[`${item.id}-${item.type}`]?.target_stock_days ?? item.target_stock_days}
                        onChange={(e) => handleTargetStockDaysChange(item, e.target.value)}
                        className="stock-input"
                        disabled={!isAdmin}
                      />
                    ) : col.key === 'stock_days' ? (
                      calculateStockDays(item)
                    ) : col.key === 'stock_health' ? (
                      <span className={`stock-health-chip ${getStockHealthClass(item)}`}>{calculateStockHealth(item)}</span>
                    ) : col.key === 'product_message' ? (
                      <span
                        className="product-name"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowProductDialog(item)}
                      >
                        {item.website_message || ''}
                      </span>
                    ) : col.key === 'estimated_restock_date' ? (
                      <span
                        className="product-name"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setShowProductDialog(item)}
                      >
                        {item.estimated_restock_date ? new Date(item.estimated_restock_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                      </span>
                    ) : null}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {showProductDialog && (
        <ProductDetailsDialog
          item={showProductDialog}
          onClose={() => setShowProductDialog(null)}
          refreshStock={() => fetchNeonStock(true)}
        />
      )}
      {activeSupplierDialog && (
        <div className="supplier-dialog-overlay">
          <div className="supplier-dialog" ref={dialogRef}>
            <h3>Select Supplier</h3>
            {activeItem && (
              <p className="supplier-dialog-subtitle">
                {getDisplayName(activeItem, displayedData, stockData)}
              </p>
            )}
            <ul className="supplier-select-list">
              {(() => {
                const currentName =
                  changedStocks[`${activeItem?.id}-${activeItem?.type}`]?.supplier_name ??
                  activeItem?.supplier_name ??
                  'None';
                return (
                  <>
                    <li
                      className={`supplier-select-item ${currentName === 'None' || !currentName ? 'current' : ''}`}
                      onClick={() => handleSupplierSelect('0')}
                    >
                      <span className="supplier-swatch" style={{ backgroundColor: '#e0e0e0' }} />
                      None
                      {(currentName === 'None' || !currentName) && <span className="supplier-current-check">✓</span>}
                    </li>
                    {suppliers.map((supplier) => (
                      <li
                        key={supplier.id}
                        className={`supplier-select-item ${currentName === supplier.name ? 'current' : ''}`}
                        onClick={() => handleSupplierSelect(supplier.name)}
                      >
                        <span className="supplier-swatch" style={{ backgroundColor: supplier.color }} />
                        {supplier.name}
                        {currentName === supplier.name && <span className="supplier-current-check">✓</span>}
                      </li>
                    ))}
                  </>
                );
              })()}
            </ul>
            <div className="supplier-dialog-actions">
              <button
                className="action-button cancel-button"
                onClick={cancelDialog}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {activeTagDialog && (
        <div className="supplier-dialog-overlay">
          <div className="supplier-dialog" ref={dialogRef}>
            <h3>Select Tags</h3>
            <ul className="tag-list">
              {tags.map((tag) => (
                <li key={tag.id} className="tag-item">
                  <label className="tag-checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedTagIds.includes(tag.id)}
                      onChange={() => handleTagToggle(tag.id)}
                    />
                    <span className="tag-chip" style={{ backgroundColor: tag.color }}>
                      {tag.name}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <div className="supplier-dialog-actions">
              <button className="action-button" onClick={handleTagSave}>
                Save
              </button>
              <button className="action-button cancel-button" onClick={cancelDialog}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {reasonDialog && (
        <div className="supplier-dialog-overlay">
          <div className="supplier-dialog" ref={dialogRef}>
            <h3>Provide Reason for Stock Changes</h3>
            <input
              type="text"
              placeholder="Enter reason for stock changes (e.g., Inventory adjustment)"
              value={reasonValue}
              onChange={(e) => setReasonValue(e.target.value)}
              className="search-input"
              autoComplete="off"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleReasonSubmit(reasonValue);
              }}
            />
            {getRaiseBackWarnings().map((w) => (
              <div key={w.sku} className="yellow-row">
                <strong>{w.name} ({w.sku})</strong>: Total Stock {w.total} is below warehouse
                stock ({w.warehouseTotal}). Tonight's sync will raise it back up (less any unshipped
                orders) and push that to WooCommerce, unless you also lower the warehouse stock.
              </div>
            ))}
            <div className="supplier-dialog-actions">
              <button
                className="action-button"
                onClick={() => handleReasonSubmit(reasonValue)}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Submit'}
              </button>
              <button
                className="action-button cancel-button"
                onClick={cancelDialog}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockTable;